/** @format */

import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuthStore } from "../../../context/useAuthStore";
import toast from "react-hot-toast";
import { MdDelete, MdEdit } from "react-icons/md";
import { Link } from "react-router-dom";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface Blog {
  _id: string;
  blogId?: string;
  title: string;
  slug: string;
  image: string;
  shortDescription: string;
}

const ListAllBlogsPage: React.FC = () => {
  const { token } = useAuthStore();
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/allblog`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
      });
      if (response.data && response.data.success) {
        setBlogs(response.data.success);
      } else if (Array.isArray(response.data)) {
        setBlogs(response.data);
      } else {
        setBlogs([]);
      }
    } catch (error) {
      console.error("Error fetching blogs:", error);
      toast.error("Failed to fetch blogs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, [token]);

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this blog post?"))
      return;
    try {
      await axios.delete(`${API_BASE_URL}/blog`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        params: { blogId: id },
        withCredentials: true,
      });
      setBlogs((prev) =>
        prev.filter((blog) => (blog.blogId || blog._id) !== id)
      );
      toast.success("Blog deleted successfully");
    } catch (error) {
      console.error("Error deleting blog:", error);
      toast.error("Failed to delete blog");
    }
  };

  return (
    <div className="subjectSection">
      <h2 className="subjectTitle">List All Blogs</h2>
      <div className="borderLine"></div>
      {loading ? (
        <p className="subjectNotFound">Loading...</p>
      ) : blogs.length === 0 ? (
        <p className="subjectNotFound">No blogs found.</p>
      ) : (
        <section className="subjectTableSection">
          <div className="overflow-x-auto">
            <div className="max-h-[calc(100vh-17.75rem)] overflow-y-auto">
              <table className="min-w-full border-collapse">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="bg-gray-200">
                    <th className="tableHeadValue">Sl.No</th>
                    <th className="tableHeadValue">Title</th>
                    <th className="tableHeadValue">Image</th>
                    <th className="tableHeadValue">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {blogs.map((blog, index) => (
                    <tr key={blog._id || index} className="tableBodyRow">
                      <td className="tableBodyValue">{index + 1}</td>
                      <td className="tableBodyValue">{blog.title}</td>
                      <td className="tableBodyValue">
                        <img
                          src={(() => {
                            if (!blog.image) return "/what-we-do.png";
                            const filename = blog.image.split("/").pop();
                            return `https://www.llb360.com/uploads/blogs/${filename}`;
                          })()}
                          alt={blog.title}
                          className="w-16 h-16 object-cover rounded"
                        />
                      </td>
                      <td className="tableBodyValue">
                        <div className="flex gap-2">
                          <Link
                            to={`/admin/edit-blog/${blog.slug}`}
                            className="bg-gray-100 text-gray-500 p-2 cursor-pointer rounded-full hover:bg-gray-500 hover:text-white transition"
                          >
                            <MdEdit className="w-5 h-5" />
                          </Link>
                          <button
                            onClick={() =>
                              handleDelete(blog.blogId || blog._id)
                            }
                            className="bg-red-100 text-red-500 p-2 cursor-pointer rounded-full hover:bg-red-500 hover:text-white transition"
                          >
                            <MdDelete className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ListAllBlogsPage;
